import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationListQuery,
  MessageListQuery,
  SendMessageInput,
  StartConversationInput,
} from '@teentack/shared';
import { notificationKeys } from '@/features/notifications/use-notifications';
import {
  fetchConversation,
  fetchConversations,
  fetchMessages,
  fetchUnreadMessageCount,
  joinConversation,
  markConversationRead,
  sendMessage,
  startConversation,
} from './messaging.api';

export const messagingKeys = {
  all: ['conversations'] as const,
  list: (query: Partial<ConversationListQuery>) => ['conversations', 'list', query] as const,
  one: (id: string) => ['conversations', 'one', id] as const,
  messages: (id: string, query: Partial<MessageListQuery>) =>
    ['conversations', 'messages', id, query] as const,
  unread: ['conversations', 'unread'] as const,
};

export function useConversations(query: Partial<ConversationListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: messagingKeys.list(query),
    queryFn: () => fetchConversations(query),
    enabled,
    staleTime: 10_000,
  });
}

export function useConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.one(conversationId ?? ''),
    queryFn: () => fetchConversation(conversationId as string),
    enabled: Boolean(conversationId),
  });
}

/**
 * An open thread polls.
 *
 * There is no realtime transport in this stack yet, so an open conversation
 * refetches every ten seconds. That is the honest version of "live" here, and
 * it is cheap: one indexed query over a single thread.
 */
export function useMessages(conversationId: string | undefined, pageSize = 50) {
  return useQuery({
    queryKey: messagingKeys.messages(conversationId ?? '', { pageSize }),
    queryFn: () => fetchMessages(conversationId as string, { pageSize }),
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? 10_000 : false,
  });
}

export function useUnreadMessageCount(enabled = true) {
  return useQuery({
    queryKey: messagingKeys.unread,
    queryFn: fetchUnreadMessageCount,
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
  });
}

export function useStartConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartConversationInput) => startConversation(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageInput) => sendMessage(conversationId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => markConversationRead(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.all });
      // Reading a thread also clears its notifications from the bell.
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useJoinConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => joinConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}
